import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled from '@emotion/styled';
import { 
  collection, 
  getDocs, 
  doc,
  addDoc,
  updateDoc,
  query, 
  orderBy,
  where,
  getDoc,
  Timestamp,
  serverTimestamp,
  deleteDoc
} from 'firebase/firestore';
import { db } from '../firebase';
import { 
  LINE_STATUSES, 
  LINE_TYPES,
  SIZES,
  sortSizes,
  ORDER_STATUSES 
} from '../firebase/schema';
import { syncOrderPaymentIfInvoiceable } from '../utils/orderPayment';
import { reasonSpLineCannotPrint } from '../utils/spLicenseStore';
import LineItemCompact from '../components/LineItemCompact';
import LineItem2 from '../components/LineItem2';
import SearchInput from '../components/SearchInput';
import QCPrintedModal from '../components/QCPrintedModal';
import { debounce } from 'lodash';
import { getMockupUrl, getPersistableMockupUrl, normalizeFilesForFirestore } from '../utils/fileHelpers';
import { mapDbTypeToLineType } from '../utils/customerDesignLibrary';
import { DOCUMENT_LINE_TYPE_ORDER } from '../utils/documentLineGroups';
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { useFacility } from '../context/FacilityContext';
import { filterByFacility } from '../utils/facilities';

// Map DB type (e.g. 'screen', 'design_only') to LINE_TYPES for display in Edit Line modal
const normalizePrintTypeForDisplay = (type) => {
  if (!type) return LINE_TYPES.SCREEN_PRINT;
  const t = String(type).toLowerCase();
  if (t === 'screen' || t === 'screen print') return LINE_TYPES.SCREEN_PRINT;
  if (t === 'embroidery') return LINE_TYPES.EMBROIDERY;
  if (t === 'dtg') return LINE_TYPES.DTG;
  if (t === 'dtf') return LINE_TYPES.DTF;
  if (t === 'design_only' || t === 'design only') return LINE_TYPES.DESIGN_ONLY;
  if (Object.values(LINE_TYPES).includes(type)) return type;
  return LINE_TYPES.SCREEN_PRINT;
};

const LINE_STATUS_SORT_ORDER = Object.values(LINE_STATUSES);

const SORT_OPTIONS = [
  { key: 'dueDate', label: 'Due Date' },
  { key: 'customerName', label: 'Customer' },
  { key: 'designName', label: 'Design' },
  { key: 'status', label: 'Status' },
  { key: 'type', label: 'Type' },
  { key: 'orderNumber', label: 'PO' },
  { key: 'totalQuantity', label: 'Pieces' }
];

const toMillis = (value) => {
  if (!value) return 0;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (typeof value.toDate === 'function') return value.toDate().getTime();
  if (value.seconds != null) return value.seconds * 1000;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

const NAVBAR_HEIGHT = 56;

const LinesContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  background: var(--al-bg);
  padding-top: ${NAVBAR_HEIGHT}px;
`;

const HeaderContainer = styled.div`
  position: sticky;
  top: ${NAVBAR_HEIGHT}px;
  background: var(--al-bg);
  padding: 12px 1% 10px;
  z-index: 100;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const TitleRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 10px;
`;

const PageHeader = styled.h1`
  font-family: 'Montserrat', sans-serif;
  color: white;
  text-transform: uppercase;
  font-size: 22px;
  margin: 0;
  font-weight: 600;
  letter-spacing: 0.05em;
`;

const TitleHint = styled.span`
  color: rgba(255, 255, 255, 0.4);
  font-size: 12px;
`;

const ControlsPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FiltersContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: stretch;
  flex-wrap: wrap;
`;

const SortBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
`;

const SortLabel = styled.span`
  color: rgba(255, 255, 255, 0.45);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-right: 2px;
`;

const SortButton = styled.button`
  background: ${props => props.active ? 'rgba(var(--al-primary-rgb), 0.15)' : 'transparent'};
  border: 1px solid ${props => props.active ? 'var(--al-primary)' : 'rgba(255, 255, 255, 0.1)'};
  color: ${props => props.active ? 'var(--al-primary)' : 'rgba(255, 255, 255, 0.7)'};
  padding: 0 10px;
  border-radius: 4px;
  font-family: 'Montserrat', sans-serif;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 36px;
  box-sizing: border-box;

  &:hover {
    border-color: var(--al-primary);
    color: var(--al-primary);
  }
`;

const ResultCount = styled.span`
  margin-left: auto;
  color: rgba(255, 255, 255, 0.4);
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const Select = styled.select`
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: ${props => props.theme.colors.text};
  padding: 0 12px;
  border-radius: 4px;
  font-family: 'Montserrat', sans-serif;
  font-size: 13px;
  min-width: 140px;
  height: 36px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }

  option {
    background: ${props => props.theme.colors.background};
    color: ${props => props.theme.colors.text};
  }
`;

const ArchiveLabel = styled.label`
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.85);
  padding: 0 12px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  user-select: none;
  white-space: nowrap;
  height: 36px;
  box-sizing: border-box;
`;

const SearchWrap = styled.div`
  flex: 1;
  min-width: 240px;

  > div {
    height: 36px;
  }

  input {
    height: 36px;
    padding-top: 0;
    padding-bottom: 0;
  }
`;

const LinesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 1% 20px 1%;
`;

const LineItem = styled.div`
  height: 100px;
  display: grid;
  grid-template-columns: 200px 100px 150px 1fr;
  background: ${props => props.theme.colors.navbar};
  gap: 8px;
  padding: 8px;
`;

const DesignNameInput = styled.input`
  width: 100%;
  height: 100%;
  background: ${props => props.theme.colors.input};
  border: 1px solid ${props => props.theme.colors.border};
  color: white;
  padding: 8px;
  font-size: 14px;
`;

const ArtworkBox = styled.div`
  height: 90px;
  width: 90px;
  margin: auto 0;
  border: 2px dashed ${props => props.theme.colors.border};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  
  &:hover {
    border-color: ${props => props.theme.colors.primary};
  }
  
  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
`;

const StyleColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StyleInput = styled.input`
  height: 45px;
  background: ${props => props.theme.colors.input};
  border: 1px solid ${props => props.theme.colors.border};
  color: white;
  padding: 8px;
`;

const InkColorSection = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 4px;
`;

const InkColorColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ColorSelect = styled.select`
  width: 100%;
  background: ${props => props.theme.colors.input};
  border: 1px solid ${props => props.theme.colors.border};
  color: white;
  padding: 4px;
`;

const ColorInput = styled.input`
  width: 100%;
  background: ${props => props.theme.colors.input};
  border: 1px solid ${props => props.theme.colors.border};
  color: white;
  padding: 4px;
`;

// Modal styled components for Edit Line
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;

const ModalBox = styled.div`
  width: 95%;
  max-width: 1200px;
  max-height: 90vh;
  overflow-y: auto;
  background-color: var(--al-bg);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background-color: var(--al-surface);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const ModalTitle = styled.h3`
  color: var(--al-primary);
  font-weight: 600;
  text-transform: uppercase;
  font-family: 'Montserrat', sans-serif;
  font-size: 1.1rem;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  padding: 4px 8px;
  line-height: 1;
  
  &:hover {
    color: var(--al-primary);
  }
`;

const ModalContent = styled.div`
  padding: 20px;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 20px;
  background-color: var(--al-surface);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const CancelButton = styled.button`
  background: none;
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: rgba(255, 255, 255, 0.7);
  padding: 10px 20px;
  cursor: pointer;
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
  
  &:hover {
    color: white;
    border-color: white;
  }
`;

const SaveButton = styled.button`
  background: var(--al-primary);
  border: none;
  color: #222;
  padding: 10px 20px;
  cursor: pointer;
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
  
  &:hover {
    background: #7FE87F;
  }
  
  &:disabled {
    background: #666;
    color: #999;
    cursor: not-allowed;
  }
`;

const LineWrapper = styled.div`
  cursor: pointer;
  transition: background 0.15s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.03);
  }
`;

const Lines = () => {
  const { facilityId } = useFacility();
  const [lines, setLines] = useState([]);
  const [allLines, setAllLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [pmFilter, setPmFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'dueDate', direction: 'asc' });
  const [products, setProducts] = useState([]);
  const [includeArchive, setIncludeArchive] = useState(false);
  const [inkCounts, setInkCounts] = useState({
    front: 1,
    back: 1,
    other: 1
  });
  const [showQCModal, setShowQCModal] = useState(false);
  const [selectedLine, setSelectedLine] = useState(null);

  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingLine, setEditingLine] = useState(null);
  const [editedLineData, setEditedLineData] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchLines = async () => {
    setLoading(true);
    try {
      const linesRef = collection(db, 'orderLines');
      // Fetch all lines and filter client-side to avoid Firestore composite index issues
      const q = query(linesRef, orderBy('createdAt', 'desc'));

      const querySnapshot = await getDocs(q);
      
      const linesWithOrders = await Promise.all(
        querySnapshot.docs.map(async (docSnapshot) => {
          const lineData = docSnapshot.data();
          if (!lineData.orderId) {
            return {
              id: docSnapshot.id,
              ...lineData,
              facilityId: lineData.facilityId,
              orderNumber: 'Unknown',
              customerName: 'Unknown',
              dueDate: null,
              projectManager: null
            };
          }
          const orderRef = doc(db, 'orders', lineData.orderId);
          const orderDoc = await getDoc(orderRef);
          const orderData = orderDoc.data() || {};
          
          return {
            id: docSnapshot.id,
            ...lineData,
            facilityId: lineData.facilityId || orderData.facilityId,
            orderNumber: orderData.poNumber || 'Unknown',
            customerName: orderData.customerName || 'Unknown',
            dueDate: orderData.dueDate || null,
            projectManager: orderData.projectManager,
            orderProvidedGoods: orderData.providedGoods === true,
            orderSample: orderData.sample === true || orderData.additionalInstructions?.sample === true
          };
        })
      );

      setAllLines(filterByFacility(linesWithOrders, facilityId));
    } catch (error) {
      console.error('Error fetching lines:', error);
    } finally {
      setLoading(false);
    }
  };

  // Memoized filtered + sorted lines
  const filteredLines = useMemo(() => {
    const searchTerm = searchQuery.toLowerCase();
    let filtered = allLines.filter(line => {
      // Apply type filter first (moved from Firestore query for reliability)
      if (typeFilter !== 'all') {
        const lineType = (line.type || '').toLowerCase();
        const filterType = typeFilter.toLowerCase();
        
        // Handle 'screen' vs 'Screen Print' mismatch
        const isScreenPrintFilter = filterType === 'screen print';
        const isScreenPrintLine = lineType === 'screen' || lineType === 'screen print';
        
        if (isScreenPrintFilter) {
          if (!isScreenPrintLine) return false;
        } else if (lineType !== filterType) {
          return false;
        }
      }

      if (pmFilter !== 'all' && (line.projectManager || '') !== pmFilter) {
        return false;
      }

      // Apply status filter
      if (statusFilter !== 'all') {
        // Special handling for "Printed" status
        if (statusFilter === LINE_STATUSES.PRINTED) {
          if (!line.printed) return false;
        } else if (statusFilter === LINE_STATUSES.COMPLETED) {
          if (line.status !== LINE_STATUSES.COMPLETED) return false;
        } else {
          // For other statuses, match exactly
          if (line.status !== statusFilter) return false;
        }
      } else if (!includeArchive) {
        // "All Active Statuses": hide printed and completed lines unless the
        // user explicitly opts in via the "Include Archive" checkbox.
        if (line.printed || line.status === LINE_STATUSES.COMPLETED) {
          return false;
        }
      }

      // If no search term, include the line (already passed status/type filters)
      if (!searchTerm) return true;

      // Basic fields search (including line ID for barcode scanning)
      const basicFieldsMatch = 
        (line.customerName || '').toLowerCase().includes(searchTerm) ||
        (line.designName || '').toLowerCase().includes(searchTerm) ||
        String(line.orderNumber || '').toLowerCase().includes(searchTerm) ||
        (line.projectManager || '').toLowerCase().includes(searchTerm) ||
        (line.id || '').toLowerCase().includes(searchTerm);

      // Search through ink colors (stored as frontInkColors, backInkColors, otherInkColors arrays)
      const inkColorsMatch = 
        (line.frontInkColors || []).some(color => color?.toLowerCase().includes(searchTerm)) ||
        (line.backInkColors || []).some(color => color?.toLowerCase().includes(searchTerm)) ||
        (line.otherInkColors || []).some(color => color?.toLowerCase().includes(searchTerm));

      // Search through garment style (stored as garmentId, may include "STYLE - Description")
      const styleMatch = 
        (line.style || '').toLowerCase().includes(searchTerm) ||
        (line.garmentId || '').toLowerCase().includes(searchTerm) ||
        (line.garmentColor || '').toLowerCase().includes(searchTerm);

      return basicFieldsMatch || inkColorsMatch || styleMatch;
    });

    filtered.sort((a, b) => {
      const dir = sortConfig.direction === 'asc' ? 1 : -1;
      let aVal;
      let bVal;

      switch (sortConfig.key) {
        case 'dueDate': {
          const aTime = toMillis(a.dueDate);
          const bTime = toMillis(b.dueDate);
          if (!aTime && !bTime) return 0;
          if (!aTime) return 1;
          if (!bTime) return -1;
          return (aTime - bTime) * dir;
        }
        case 'customerName':
          aVal = (a.customerName || '').toLowerCase();
          bVal = (b.customerName || '').toLowerCase();
          break;
        case 'designName':
          aVal = (a.designName || a.design || '').toLowerCase();
          bVal = (b.designName || b.design || '').toLowerCase();
          break;
        case 'status':
          aVal = LINE_STATUS_SORT_ORDER.indexOf(a.status);
          bVal = LINE_STATUS_SORT_ORDER.indexOf(b.status);
          if (aVal === -1) aVal = 99;
          if (bVal === -1) bVal = 99;
          break;
        case 'type': {
          const aType = mapDbTypeToLineType(a.type);
          const bType = mapDbTypeToLineType(b.type);
          aVal = DOCUMENT_LINE_TYPE_ORDER.indexOf(aType);
          bVal = DOCUMENT_LINE_TYPE_ORDER.indexOf(bType);
          if (aVal === -1) aVal = 99;
          if (bVal === -1) bVal = 99;
          break;
        }
        case 'orderNumber':
          aVal = String(a.orderNumber || '');
          bVal = String(b.orderNumber || '');
          break;
        case 'totalQuantity':
          aVal = Number(a.totalQuantity) || 0;
          bVal = Number(b.totalQuantity) || 0;
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return -1 * dir;
      if (aVal > bVal) return 1 * dir;
      return 0;
    });

    return filtered;
  }, [allLines, searchQuery, statusFilter, typeFilter, pmFilter, includeArchive, sortConfig]);

  // Update displayed lines whenever filtered results change
  useEffect(() => {
    setLines(filteredLines);
  }, [filteredLines]);

  // Fetch lines on mount and when active facility changes
  useEffect(() => {
    fetchLines();
  }, [facilityId]);

  // Debounced search handler
  const handleSearchChange = useCallback(
    debounce((value) => {
      setSearchQuery(value);
    }, 300),
    []
  );

  // Handle filter changes
  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
  };

  const handleTypeFilterChange = (e) => {
    setTypeFilter(e.target.value);
  };

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: key === 'dueDate' ? 'asc' : 'asc' };
    });
  };

  const projectManagers = useMemo(() => {
    const names = new Set();
    allLines.forEach((line) => {
      const pm = (line.projectManager || '').trim();
      if (pm) names.add(pm);
    });
    return [...names].sort((a, b) => a.localeCompare(b));
  }, [allLines]);

  useEffect(() => {
    const fetchProducts = async () => {
      const productsRef = collection(db, 'products');
      const snapshot = await getDocs(productsRef);
      // Store full product data including brand and styleNo for lookups
      setProducts(snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })));
    };
    fetchProducts();
  }, []);

  const handleLineUpdate = async (lineId, updates) => {
    // If marking as printed, show QC modal
    if ('printed' in updates && updates.printed) {
      const line = lines.find(l => l.id === lineId);
      setSelectedLine(line);
      setShowQCModal(true);
      return;
    }

    try {
      const lineRef = doc(db, 'orderLines', lineId);
      await updateDoc(lineRef, {
        ...updates,
        updatedAt: serverTimestamp()
      })

      // Update local state instead of fetching all lines again
      setLines(prevLines => 
        prevLines.map(line => 
          line.id === lineId 
            ? { ...line, ...updates, updatedAt: serverTimestamp() }
            : line
        )
      );
    } catch (error) {
      console.error('Error updating line:', error);
      alert('Failed to update line');
    }
  };

  const handleQCComplete = async (lineId, status, data) => {
    try {
      const lineRef = doc(db, 'orderLines', lineId);
      const lineDoc = await getDoc(lineRef);
      const lineData = lineDoc.data();

      if (status === 'PRINTED' || status === 'PARTIALLY_PRINTED' || status === 'REDUCED') {
        const blockReason = await reasonSpLineCannotPrint(db, lineData);
        if (blockReason) {
          alert(blockReason);
          return;
        }
      }
      
      if (status === 'PRINTED') {
        // Update line to completed status
        await updateDoc(lineRef, {
          status: LINE_STATUSES.COMPLETED,
          printed: true,
          updatedAt: serverTimestamp()
        });

        // If this is a reprint line, delete it after marking as printed
        if (lineData.isReprint) {
          await deleteDoc(lineRef);
        }

        // Check if all lines in the order are printed (excluding deleted reprints)
        const orderLinesQuery = query(
          collection(db, 'orderLines'),
          where('orderId', '==', lineData.orderId)
        );
        const orderLinesSnapshot = await getDocs(orderLinesQuery);
        const allLinesPrinted = orderLinesSnapshot.docs.every(doc => doc.data().printed);

        if (allLinesPrinted) {
          const orderRef = doc(db, 'orders', lineData.orderId);
          const orderSnap = await getDoc(orderRef);
          if (orderSnap.exists()) {
            const currentOrderStatus = orderSnap.data()?.status;
            const terminalStatuses = [
              ORDER_STATUSES.READY_TO_INVOICE,
              ORDER_STATUSES.COMPLETED,
              ORDER_STATUSES.CANCELLED,
              ORDER_STATUSES.WAITING_ON_REPRINT
            ];

            if (!terminalStatuses.includes(currentOrderStatus)) {
              // Update active orders to READY_TO_INVOICE when all lines are printed.
              await updateDoc(orderRef, {
                status: ORDER_STATUSES.READY_TO_INVOICE,
                updatedAt: serverTimestamp()
              });
              await syncOrderPaymentIfInvoiceable(db, lineData.orderId);
            }
          }
        }
      } else if (status === 'PARTIALLY_PRINTED' && data?.reprintLine) {
        // Handle reprint creation
        await updateDoc(lineRef, {
          status: LINE_STATUSES.COMPLETED,
          printed: true,
          updatedAt: serverTimestamp(),
          needsReorder: true
        });

        // Fetch the original line data
        const originalLineDoc = await getDoc(doc(db, 'orderLines', lineId));
        const originalLineData = originalLineDoc.data();

        // Create new reprint line
        await addDoc(collection(db, 'orderLines'), {
          ...originalLineData,
          sizes: data.reprintLine.sizes,
          totalQuantity: data.reprintLine.totalQuantity,
          status: LINE_STATUSES.READY_TO_ORDER,
          isReprint: true,
          originalLineId: lineId,
          designName: `Reprint - ${originalLineData.designName || 'Unknown'}`,
          printed: false,
          isIn: false,
          isCut: false,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });

        // Set order status to WAITING_ON_REPRINT
        const orderRef = doc(db, 'orders', originalLineData.orderId);
        await updateDoc(orderRef, {
          status: ORDER_STATUSES.WAITING_ON_REPRINT,
          updatedAt: serverTimestamp()
        });
      } else if (status === 'REDUCED') {
        // Update line with reduced quantities
        await updateDoc(lineRef, {
          status: LINE_STATUSES.COMPLETED,
          printed: true,
          sizes: data.originalLine.sizes,
          totalQuantity: data.originalLine.totalQuantity,
          updatedAt: serverTimestamp()
        });
      }
      
      setShowQCModal(false);
      fetchLines(); // Refresh the list
    } catch (error) {
      console.error('Error updating line:', error);
      alert('Error processing line update: ' + error.message);
    }
  };

  // Helper to determine size type for a line
  const getSizeTypeForLine = (line) => {
    const sizes = line.sizes || {};
    const sizeKeys = Object.keys(sizes).filter(k => sizes[k] > 0);
    
    if (sizeKeys.some(s => s.startsWith('Y'))) return 'youth';
    if (sizeKeys.some(s => ['2', '3', '4', '5', '6', '7'].includes(s) || s.includes('T'))) return 'toddler';
    if (sizeKeys.some(s => ['NewBorn', 'NB', '3-6', '6-12', '12-18', '18-24'].includes(s))) return 'infant';
    if (sizeKeys.length === 1 && sizeKeys[0] === 'ONESIZE') return 'onesize';
    return line.sizeType || 'unisex';
  };

  // Handle opening the edit modal
  const handleEditLine = (line) => {
    // Convert line data to LineItem2 format (normalize type so Print Type dropdown shows correctly)
    const lineItem2Data = {
      id: line.id,
      design: line.designName,
      designName: line.designName,
      type: normalizePrintTypeForDisplay(line.type),
      style: line.garmentId,
      color: line.garmentColor,
      sizeType: getSizeTypeForLine(line),
      sizes: line.sizes || {},
      price: line.pricePerPiece,
      pricePerPiece: line.pricePerPiece,
      cogs: line.cogs,
      frontInkColors: line.frontInkColors || [],
      backInkColors: line.backInkColors || [],
      otherInkColors: line.otherInkColors || [],
      printLocations: line.printLocations || [],
      inkNotes: line.inkNotes,
      files: line.files,
      // Pass through other fields
      ...line,
      // Open expanded by default for editing
      isExpanded: true
    };
    setEditingLine(line);
    setEditedLineData(lineItem2Data);
    setEditModalOpen(true);
  };

  // Handle line data updates from LineItem2
  const handleLineDataUpdate = (updatedData) => {
    setEditedLineData(updatedData);
  };

  // Handle saving the edited line
  const handleSaveEditedLine = async () => {
    if (!editingLine || !editedLineData) return;
    
    setSaving(true);
    try {
      const lineRef = doc(db, 'orderLines', editingLine.id);
      
      // Calculate total quantity from sizes
      const totalQuantity = editedLineData.sizes 
        ? Object.values(editedLineData.sizes).reduce((sum, qty) => sum + (Number(qty) || 0), 0)
        : 0;
      
      // Get mockup URL - only persist if not a blob (blob URLs break in Firestore)
      const mockupUrl = getMockupUrl(editedLineData);
      const persistableMockup = getPersistableMockupUrl(mockupUrl);
      const hasBrokenBlob = editedLineData.designFileUrl && String(editedLineData.designFileUrl).startsWith('blob:');
      
      // Normalize files for Firestore (removes File objects, etc.)
      const normalizedFiles = normalizeFilesForFirestore(editedLineData.files);
      
      // Build update object - IMPORTANT: Include files and designFileUrl to preserve them
      const updateData = {
        designName: editedLineData.design || editedLineData.designName,
        garmentId: editedLineData.style,
        garmentColor: editedLineData.color,
        sizeType: editedLineData.sizeType,
        sizes: editedLineData.sizes || {},
        totalQuantity,
        pricePerPiece: parseFloat(editedLineData.price || editedLineData.pricePerPiece) || 0,
        cogs: parseFloat(editedLineData.cogs) || 0,
        type: editedLineData.type,
        frontInkColors: editedLineData.frontInkColors || [],
        backInkColors: editedLineData.backInkColors || [],
        otherInkColors: editedLineData.otherInkColors || [],
        printLocations: editedLineData.printLocations || [],
        inkNotes: editedLineData.inkNotes || null,
        updatedAt: serverTimestamp(),
        // Preserve file-related fields; never save blob URLs; clear existing blob if present
        ...(normalizedFiles && { files: normalizedFiles }),
        ...(persistableMockup ? { designFileUrl: persistableMockup } : hasBrokenBlob ? { designFileUrl: null } : {})
      };
      
      await updateDoc(lineRef, updateData);
      
      // Refresh lines to show updated data
      await fetchLines();
      
      // Close modal
      setEditModalOpen(false);
      setEditingLine(null);
      setEditedLineData(null);
      
      console.log('✅ Line updated successfully');
    } catch (error) {
      console.error('Error saving line:', error);
      alert('Failed to save changes: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  // Handle closing the edit modal
  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setEditingLine(null);
    setEditedLineData(null);
  };

  return (
    <LinesContainer>
      <HeaderContainer>
        <TitleRow>
          <PageHeader>CHECKLIST</PageHeader>
          <TitleHint>Click a line to edit</TitleHint>
        </TitleRow>

        <ControlsPanel>
        <FiltersContainer>
          <SearchWrap>
            <SearchInput
              placeholder="Search customer, design, PO, manager, ink, or style..."
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </SearchWrap>
          
          <Select 
            value={statusFilter} 
            onChange={handleStatusFilterChange}
            disabled={loading}
          >
            <option value="all">All Active Statuses</option>
            {Object.entries(LINE_STATUSES).map(([key, value]) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </Select>

          <Select 
            value={typeFilter} 
            onChange={handleTypeFilterChange}
            disabled={loading}
          >
            <option value="all">All Types</option>
            {Object.values(LINE_TYPES).map(type => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </Select>

          <Select
            value={pmFilter}
            onChange={(e) => setPmFilter(e.target.value)}
            disabled={loading}
          >
            <option value="all">All Managers</option>
            {projectManagers.map((pm) => (
              <option key={pm} value={pm}>
                {pm}
              </option>
            ))}
          </Select>

          <ArchiveLabel>
            <input
              type="checkbox"
              checked={includeArchive}
              onChange={(e) => setIncludeArchive(e.target.checked)}
              style={{
                accentColor: 'var(--al-primary)',
                width: '14px',
                height: '14px',
                cursor: 'pointer'
              }}
            />
            Include Archive
          </ArchiveLabel>
        </FiltersContainer>

        <SortBar>
          <SortLabel>Sort</SortLabel>
          {SORT_OPTIONS.map(({ key, label }) => {
            const active = sortConfig.key === key;
            return (
              <SortButton
                key={key}
                type="button"
                active={active}
                onClick={() => handleSort(key)}
              >
                {label}
                {active ? (sortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />) : <FaSort />}
              </SortButton>
            );
          })}
          <ResultCount>{lines.length} line{lines.length === 1 ? '' : 's'}</ResultCount>
        </SortBar>
        </ControlsPanel>
      </HeaderContainer>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '20px', color: 'white' }}>
          Loading...
        </div>
      ) : (
      <LinesList>
          {lines.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px', color: 'white' }}>
              No lines found
            </div>
          ) : (
            lines.map(line => (
              <LineWrapper key={line.id} onClick={() => handleEditLine(line)}>
                <LineItemCompact
                  line={line}
                  onLineUpdate={handleLineUpdate}
                  products={products}
                />
              </LineWrapper>
            ))
          )}
      </LinesList>
      )}

      {showQCModal && (
        <QCPrintedModal
          line={selectedLine}
          onClose={() => setShowQCModal(false)}
          onComplete={handleQCComplete}
        />
      )}

      {/* Edit Line Modal */}
      {editModalOpen && (
        <ModalOverlay onClick={handleCloseEditModal}>
          <ModalBox onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>✏️ Edit Line Item</ModalTitle>
              <CloseButton onClick={handleCloseEditModal}>×</CloseButton>
            </ModalHeader>

            <ModalContent>
              {editedLineData && (
                <LineItem2
                  lineData={editedLineData}
                  onUpdate={handleLineDataUpdate}
                  isNew={false}
                  onDelete={() => {}} // Don't allow delete from this view
                  onDuplicate={() => {}} // Don't allow duplicate from this view
                  isExpanded={true}
                  lineId={editingLine?.id}
                />
              )}
            </ModalContent>

            <ModalActions>
              <CancelButton onClick={handleCloseEditModal}>
                Cancel
              </CancelButton>
              <SaveButton 
                onClick={handleSaveEditedLine}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </SaveButton>
            </ModalActions>
          </ModalBox>
        </ModalOverlay>
      )}
    </LinesContainer>
  );
};

export default Lines; 